<template>
  <div
    class="summary-card"
    :class="[borderClass, { selected, compact }]"
    @click="emit('click')"
  >
    <div class="summary-card-header">
      <div class="summary-card-title-row">
        <DpplSvg
          class="summary-card-icon"
          :class="iconClass"
          :file-name="icon"
        />
        <span class="text-h3 text-dark summary-card-label">{{ label }}</span>
      </div>
      <InsightsTooltip
        :tooltip="tooltip"
        class="summary-tooltip"
        @click.stop
      />
    </div>
    <p class="summary-card-value text-darkest">
      {{ numberWithCommas(value) }}
    </p>
    <p class="text-body-1 text-dark summary-card-caption">
      employees
    </p>
  </div>
</template>

<script setup>
import { numberWithCommas } from '@/helpers/formatters';
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import DpplSvg from '@/components/datapeople/DpplSvg.vue';

defineProps({
  icon: { type: String, required: true },
  iconClass: { type: String, default: '' },
  borderClass: { type: String, default: '' },
  label: { type: String, required: true },
  value: { type: Number, default: 0 },
  tooltip: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits(['click']);
</script>

<style lang="less" scoped>
.summary-card {
  position: relative;
  min-width: 0;
  height: 136px;
  padding: 16px;
  border-radius: 6px;
  border: 1px solid;
  background: @white;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  cursor: pointer;

  &.border-no-acknowledgement {
    border-color: @red-700;

    &.selected {
      background: @red-100;
    }
  }

  &.border-incomplete {
    border-color: @orange-700;

    &.selected {
      background: @orange-100;
    }
  }

  &.border-job-profile-changed {
    border-color: @yellow-700;

    &.selected {
      background: @yellow-100;
    }
  }

  &.border-out-of-date {
    border-color: @navy-500;

    &.selected {
      background: @navy-100;
    }
  }

  &:hover {
    box-shadow:
      0 30px 33px 0 fade(@black, 5%),
      0 22px 17px 0 fade(@black, 4%),
      0 12px 10px 0 fade(@black, 4%),
      0 2px 2px 0 fade(@black, 2%),
      0 6px 5px 0 fade(@black, 3%);
  }

  &.compact {
    .summary-card-label {
      display: none;
    }

    .summary-tooltip {
      display: none;
    }
  }
}

.summary-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  width: 100%;
  min-height: 28px;
}

.summary-card-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.summary-card-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-card-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;

  &.icon-no-acknowledgement {
    color: @red-700;
  }

  &.icon-incomplete {
    color: @orange-700;
  }

  &.icon-job-profile-changed {
    color: @yellow-700;
  }

  &.icon-out-of-date {
    color: @navy-500;
  }
}

.summary-tooltip {
  flex-shrink: 0;
}

.summary-card-value {
  font-size: 36px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 4px;
}

.summary-card-caption {
  font-weight: 400;
}
</style>
